import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const SideNav = ({ isOpen, onToggle, onCollapseChange }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  
  // Set initial collapsed state based on localStorage or default to false
  useEffect(() => {
    const savedState = localStorage.getItem('sideNavCollapsed');
    if (savedState !== null) {
      setCollapsed(savedState === 'true');
    }
  }, []);
  
  // Save collapsed state to localStorage and notify parent component
  useEffect(() => {
    localStorage.setItem('sideNavCollapsed', collapsed);
    if (onCollapseChange) {
      onCollapseChange(collapsed);
    }
  }, [collapsed, onCollapseChange]);

  const navigationItems = [
    { 
      label: 'Dashboard', 
      path: '/dashboard', 
      icon: 'LayoutDashboard',
      tooltip: 'View inventory overview and alerts'
    },
    { 
      label: 'Products', 
      path: '/product-catalog', 
      icon: 'Package',
      tooltip: 'Manage product catalog and inventory'
    },

    { 
      label: 'Checkout', 
      path: '/checkout', 
      icon: 'CreditCard',
      tooltip: 'Scan and checkout items'
    },
    { 
      label: 'Sold Items', 
      path: '/sold-items', 
      icon: 'ShoppingBag',
      tooltip: 'View items that have been sold or used'
    },
    { 
      label: 'Activity Log', 
      path: '/activity-log', 
      icon: 'Activity',
      tooltip: 'View system activity and audit trail'
    },
    { 
      label: 'Reports', 
      path: '/reports', 
      icon: 'BarChart3',
      tooltip: 'Analytics and business intelligence'
    },
    { 
      label: 'Settings', 
      path: '/settings', 
      icon: 'Settings',
      tooltip: 'Configure application settings'
    },

    { 
      label: 'Help', 
      path: '/help', 
      icon: 'HelpCircle',
      tooltip: 'Get help and support'
    }
  ];

  const handleNavigation = (path) => {
    navigate(path);
    if (window.innerWidth < 768) {
      onToggle(false); // Close side nav on mobile after navigation
    }
  };

  const isActivePath = (path) => {
    if (path === '/product-catalog') {
      return location?.pathname === '/product-catalog' || location?.pathname === '/product-details';
    }
    return location?.pathname === path;
  };

  const toggleCollapse = () => {
    setCollapsed(!collapsed);
  };

  // Don't render if not open on mobile
  if (!isOpen && window.innerWidth < 768) {
    return null;
  }

  return (
    <>
      {/* Overlay for mobile */}
      {isOpen && window.innerWidth < 768 && (
        <div 
          className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm transition-all duration-300" 
          onClick={() => onToggle(false)}
        />
      )}
      
      {/* Side Navigation */}
      <div 
        className={`fixed top-14 sm:top-16 left-0 bottom-0 z-40 bg-card border-r border-border shadow-xl transition-all duration-300 ${collapsed ? 'w-16' : 'w-64'} ${isOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`}
        data-collapsed={collapsed}
      >
        <div className="flex flex-col h-full">
          {/* Navigation Items */}
          <nav className="flex-1 py-4 overflow-y-auto">
            <ul className="space-y-1 px-2">
              {navigationItems.map((item) => (
                <li key={item.path}>
                  <Button
                    variant={isActivePath(item.path) ? "default" : "ghost"}
                    onClick={() => handleNavigation(item.path)}
                    className={`w-full justify-${collapsed ? 'center' : 'start'} px-3 py-2 ${collapsed && !isActivePath(item.path) ? 'hover:bg-primary/10' : ''}`}
                    title={collapsed ? item.tooltip : ''}
                  >
                    <div className={`${collapsed ? 'p-1 rounded-md' : ''} ${collapsed && isActivePath(item.path) ? 'bg-background/80' : ''}`}>
                      <Icon name={item.icon} size={collapsed ? 24 : 20} className={collapsed ? "" : "mr-2"} />
                    </div>
                    {!collapsed && <span>{item.label}</span>}
                  </Button>
                </li>
              ))}
            </ul>
          </nav>
          
          {/* Collapse/Expand Button */}
          <div className="p-4 border-t border-border">
            <Button
              variant="ghost"
              onClick={toggleCollapse}
              className={`w-full justify-${collapsed ? 'center' : 'start'} px-3 py-2 relative`}
              title={collapsed ? 'Expand' : 'Collapse'}
            >
              <Icon name={collapsed ? 'ChevronRight' : 'ChevronLeft'} size={collapsed ? 24 : 20} className={collapsed ? '' : 'mr-2'} />
              {!collapsed && <span>Collapse</span>}
              {collapsed && (
                <div className="absolute right-1 top-1/2 transform -translate-y-1/2 bg-primary/10 rounded-full p-1">
                  <Icon name="ArrowRight" size={14} className="text-primary" />
                </div>
              )}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default SideNav;